import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Loading } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const BUSINESS_TYPES = [
  "Proprietorship",
  "Partnership",
  "LLP",
  "Private Limited",
  "Public Limited",
  "Individual / Service Professional",
  "Other",
];

const FIELDS = [
  ["business_name", "Business / Firm Name", true],
  ["contact_person", "Contact Person", true],
  ["email", "Business Email", true],
  ["mobile", "Mobile", true],
  ["alternate_mobile", "Alternate Mobile", false],
  ["address", "Address", true],
  ["city", "City", true],
  ["district", "District", false],
  ["state", "State", true],
  ["pincode", "PIN Code", false],
  ["website", "Website", false],
  ["experience", "Years in Business", false],
];

export default function VendorProfile() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/vendor/profile").then((response) => setProfile(response.data));
  }, []);

  if (!profile) return <Loading />;

  const set = (key, value) => setProfile((current) => ({ ...current, [key]: value }));
  const save = async () => {
    setSaving(true);
    try {
      const response = await api.put("/vendor/profile", profile);
      setProfile(response.data);
      toast.success("Business profile saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save your profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="max-w-5xl" data-testid="vendor-profile-page">
      <div className="border-b border-slate-200 pb-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Business profile</p>
        <h1 className="mt-2 font-display text-3xl font-bold">Business details</h1>
        <p className="mt-2 text-sm text-slate-600">Keep your contact and service identity current for verification.</p>
      </div>
      <div className="mt-6 grid gap-5 border border-slate-200 bg-white p-5 sm:grid-cols-2 sm:p-7">
        <div className="sm:col-span-2 grid gap-4 border-b border-slate-100 pb-5 sm:grid-cols-3">
          <ReadOnly label="Vendor ID" testId="vendor-profile-id" value={profile.vendor_display_id} />
          <ReadOnly label="Application" testId="vendor-profile-application-status" value={profile.application_status} />
          <ReadOnly label="Verification" testId="vendor-profile-verification-status" value={profile.vendor_verification_status} />
        </div>
        {FIELDS.map(([key, label, required]) => (
          <div key={key} className={key === "address" ? "sm:col-span-2" : ""}>
            <Label className="mb-1.5 block" htmlFor={`vendor-profile-${key}`}>
              {label}{required ? " *" : ""}
            </Label>
            <Input
              data-testid={`vendor-profile-${key}`}
              id={`vendor-profile-${key}`}
              onChange={(event) => set(key, event.target.value)}
              value={profile[key] || ""}
            />
          </div>
        ))}
        <div>
          <Label className="mb-1.5 block" htmlFor="vendor-profile-business-type">Business Type</Label>
          <select
            className="h-10 w-full border border-slate-300 bg-white px-3 text-sm"
            data-testid="vendor-profile-business-type"
            id="vendor-profile-business-type"
            onChange={(event) => set("business_type", event.target.value)}
            value={profile.business_type || ""}
          >
            <option value="">Select business type</option>
            {BUSINESS_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <Label className="mb-1.5 block" htmlFor="vendor-profile-description">Business Description</Label>
          <Textarea
            data-testid="vendor-profile-description"
            id="vendor-profile-description"
            onChange={(event) => set("description", event.target.value)}
            value={profile.description || ""}
          />
        </div>
        <div className="sm:col-span-2 flex justify-end border-t border-slate-100 pt-5">
          <Button data-testid="vendor-profile-save-button" disabled={saving} onClick={save} type="button">
            {saving ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </div>
      <VendorAgreementsSection />
    </section>
  );
}

function VendorAgreementsSection() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/vendor/legal/acceptances/mine")
      .then((r) => setRows(r.data.acceptances || []))
      .catch(() => setRows([]));
  }, []);
  if (rows === null) return null;
  const download = async (acceptanceId) => {
    try {
      const response = await api.get(`/vendor/legal/acceptances/${acceptanceId}/pdf`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `agreement-${acceptanceId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Could not download the agreement PDF");
    }
  };
  return (
    <div className="mt-10 border-t border-slate-200 pt-6" data-testid="vendor-agreement-history">
      <h3 className="font-display text-lg font-semibold">Accepted agreements &amp; schedules</h3>
      <p className="text-xs text-slate-500 mt-1">The exact agreement text and commercial schedule in effect at the time of each acceptance are preserved and downloadable.</p>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500 mt-4">No acceptances recorded yet.</p>
      ) : (
        <table className="w-full text-sm border mt-4">
          <thead className="bg-slate-50">
            <tr>
              <th className="p-2 text-left">Accepted on</th>
              <th className="p-2 text-left">Agreement</th>
              <th className="p-2 text-left">Schedule</th>
              <th className="p-2 text-left">Source</th>
              <th className="p-2 text-left">PDF</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.acceptance_id} className="border-t" data-testid={`vendor-agreement-row-${row.acceptance_id}`}>
                <td className="p-2">{row.server_ts}</td>
                <td className="p-2 font-mono text-xs">v{row.agreement_version} ({row.agreement_status_at_acceptance})</td>
                <td className="p-2 font-mono text-xs">v{row.schedule_version} ({row.schedule_status_at_acceptance})</td>
                <td className="p-2">{row.source}</td>
                <td className="p-2">
                  <Button variant="outline" size="sm" onClick={() => download(row.acceptance_id)} data-testid={`vendor-agreement-pdf-${row.acceptance_id}`}>
                    Download PDF
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ReadOnly({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-medium">{String(value || "Not available").replaceAll("_", " ")}</p>
    </div>
  );
}